const MARUTI_CAR_MAKER = "maruti";

export type DigitalEstimateEligibilityInput = {
  carMaker?: string | null;
  customerArea?: string | null;
  enforcedCustomerAreas?: string[] | null;
  hasSharedEstimateForEnquiry: boolean;
};

const normalizeId = (value: unknown): string => String(value ?? "").trim();

/**
 * Outer gate: the customer's area is listed in
 * `enforce_digital_estimate_customer_areas` (case-insensitive).
 */
export function isCustomerInDigitalEstimateArea(
  customerArea: string | null | undefined,
  enforcedCustomerAreas: string[] | null | undefined,
): boolean {
  const area = normalizeId(customerArea).toLowerCase();
  if (!area) {
    return false;
  }
  return (enforcedCustomerAreas ?? []).some(
    (enforcedArea) => normalizeId(enforcedArea).toLowerCase() === area,
  );
}

export function isMarutiCarMaker(carMaker?: string | null): boolean {
  return normalizeId(carMaker).toLowerCase() === MARUTI_CAR_MAKER;
}

/**
 * True when a customer in an enforced area must push digital estimate before
 * Proceed / Hold / Cancel (Maruti + no SHARED for enquiry).
 */
export function requiresDigitalEstimateShare(
  input: DigitalEstimateEligibilityInput,
): boolean {
  if (
    !isCustomerInDigitalEstimateArea(
      input.customerArea,
      input.enforcedCustomerAreas,
    )
  ) {
    return false;
  }
  if (!isMarutiCarMaker(input.carMaker)) {
    return false;
  }
  if (input.hasSharedEstimateForEnquiry) {
    return false;
  }
  return true;
}

/**
 * Push disable reason for customers in an enforced area only.
 * Returns empty string when Push should follow existing technical gates
 * (enforce-share state) or when the customer is outside the enforced areas.
 *
 * If any estimate for the enquiry is already SHARED, that reason always wins
 * over Maruti / other digital-estimate messages.
 */
export function getDigitalEstimateIneligibilityReason(
  input: DigitalEstimateEligibilityInput,
): string {
  if (
    !isCustomerInDigitalEstimateArea(
      input.customerArea,
      input.enforcedCustomerAreas,
    )
  ) {
    return "";
  }

  if (input.hasSharedEstimateForEnquiry) {
    return "An estimate was already shared for this enquiry";
  }
  if (!isMarutiCarMaker(input.carMaker)) {
    return "Digital estimate is only for Maruti car makers";
  }
  return "";
}

/** True when Proceed/Hold/Cancel must wait for a digital estimate push. */
export function isProceedBlockedByDigitalEstimate(
  input: DigitalEstimateEligibilityInput,
): boolean {
  return requiresDigitalEstimateShare(input);
}

export function hasSharedEstimateInList(
  estimates: Array<{ status?: string | null }> | null | undefined,
): boolean {
  return (estimates ?? []).some(
    (estimate) =>
      normalizeId(estimate?.status).toUpperCase() === "SHARED",
  );
}
